/**
 * TemplateModal — Reusable template picker modal.
 * Used for both the Job template picker and the Gig template picker —
 * pass different `templates`, `title`, `icon`, and colour props.
 *
 * Props:
 *   open          bool
 *   onClose       () => void
 *   title         string                 e.g. "Job template"
 *   subtitle      string
 *   icon          ReactElement           e.g. <IconSparkles size={20} />
 *   iconBg        string                 CSS background (gradient or colour)
 *   iconColor     string
 *   templates     Template[]             { role, icon, ...prefillFields }
 *   searchPlaceholder  string
 */

import Button from "../UI/Button/button";
import { useEffect, useMemo, useRef, useState } from "react";
import { clientGetTemplate } from "../../services/clientCalls";
import styles from "./jobTemplatePopup.module.css";
import {
  IconBolt,
  IconBriefcase,
  IconSearch,
  IconX,
} from "@tabler/icons-react";

const JobTemplatePopup = ({
  jobTemplate,
  setJobTemplate,
  open,
  onClose,
  title,
  iconBg = "linear-gradient(135deg,#eff2ff,#dde3ff)",
  iconColor = "#1d4ed8",
  searchPlaceholder = "Search for a role…",
  jobType,
  setJobNotice,
}) => {
  const [allTalentRoles, setTalentRoles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState(null);
  const inputRef = useRef(null);

  const roleOptions = useMemo(
    () =>
      allTalentRoles.map((r) => ({
        description: r.description,
        objectives: r.objectives,
        acceptanceCriteria: r.acceptanceCriteria,
        skillsAndRequirements: r.skillsAndRequirements,
        title: r.title,
      })),
    [allTalentRoles],
  );

  useEffect(() => {
    let isMounted = true;

    const fetchTalentRoles = async () => {
      setLoading(true);
      try {
        const res = await clientGetTemplate();
        if (isMounted && res?.data?.data?.data) {
          setTalentRoles(res.data.data.data);
        }
      } catch (e) {
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchTalentRoles();

    return () => {
      isMounted = false;
    };
  }, []);

  // Reset + autofocus whenever the modal opens
  useEffect(() => {
    if (open) {
      setQuery("");
      setSelected(null);
      setTimeout(() => inputRef.current?.focus(), 180);
    }
  }, [open]);

  // Lock body scroll while open
  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [open, onClose]);

  const suggestions = useMemo(() => {
    if (!query.trim()) return roleOptions.slice(0, 10);

    return roleOptions.filter((option) =>
      option.title.toLowerCase().includes(query.toLowerCase()),
    );
  }, [query, roleOptions]);

  const handleApply = () => {
    if (!selected) return;
    // onApply(selected);
    setJobTemplate(selected);
    setJobNotice({
      show: true,
      text: `Template applied: ${selected?.title}`,
    });
    onClose();
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  if (!title) return null; // guard against misuse without required props

  return (
    <div
      className={`${styles.backdrop} ${open ? styles.backdropOpen : ""}`}
      onClick={handleBackdropClick}
    >
      <div className={styles.card}>
        {/* Header */}
        <div className={styles.head}>
          <div className={styles.headTop}>
            <div className={styles.headLeft}>
              <div
                className={styles.headIcon}
                style={{ background: iconBg, color: iconColor }}
              >
                <IconBriefcase size={21} />
              </div>
              <div>
                <div className={styles.headTitle}>{title}</div>
                {jobType?.toLowerCase() === "job" && (
                  <div className={styles.headSub}>
                    Search a role to pre-fill your form, or start from scratch.
                  </div>
                )}
                {jobType?.toLowerCase() === "gig" && (
                  <div className={styles.headSub}>
                    Search a gig type to pre-fill your form, or start from
                    scratch.
                  </div>
                )}
              </div>
            </div>
            <button
              className={styles.closeBtn}
              onClick={onClose}
              aria-label="Close"
            >
              <IconX size={15} />
            </button>
          </div>
        </div>
        {/* Search field */}
        <div className={styles.searchBody}>
          <div
            className={styles.searchField}
            onClick={() => inputRef.current?.focus()}
          >
            {!selected && (
              <IconSearch size={18} className={styles.searchFieldIcon} />
            )}

            {!selected ? (
              <input
                ref={inputRef}
                type="search"
                className={styles.searchInput}
                placeholder={
                  jobType?.toLowerCase() === "gig"
                    ? "Search a gig type..."
                    : searchPlaceholder
                }
                autoComplete="off"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
            ) : (
              <div className={styles.selPill}>
                <div
                  className={styles.selChip}
                  style={selected ? { background: iconColor } : undefined}
                >
                  <IconBriefcase size={14} />
                  <span>{selected.title}</span>
                </div>
                <button
                  type="button"
                  className={styles.pillX}
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelected(null);
                    setTimeout(() => inputRef.current?.focus(), 50);
                  }}
                >
                  <IconX size={11} />
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Suggestions */}
        <div className={styles.sugs}>
          {!selected && suggestions.length === 0 && (
            <span className={styles.sugHint}>
              No matches — try a different keyword.
            </span>
          )}
          {!selected &&
            suggestions.map((t) => (
              <button
                key={t?.title}
                type="button"
                className={styles.sug}
                onClick={() => setSelected(t)}
              >
                {t?.title}
              </button>
            ))}
        </div>

        {/* Footer */}
        <div className={styles.foot}>
          <Button variant="ghost" onClick={onClose}>
            Start from scratch
          </Button>
          <Button
            variant="secondary"
            leftIcon={<IconBolt size={15} />}
            disabled={!selected}
            onClick={handleApply}
          >
            Apply Template
          </Button>
        </div>
      </div>
    </div>
  );
};

export default JobTemplatePopup;
